import React, { createContext, useEffect, useState, useCallback } from "react";
import { getMyData } from "../api/auth";

let logoutTimer;

const noopStorage = {
  getItem: () => null,
  setItem: () => {},
  removeItem: () => {},
};

const resolveStorage = () => {
  if (typeof window === "undefined") {
    return noopStorage;
  }
  try {
    const { localStorage } = window;
    const testKey = "__storage_test__";
    localStorage.setItem(testKey, "1");
    localStorage.removeItem(testKey);
    return localStorage;
  } catch (error) {
    console.warn(
      "Local storage is not available; applying in-memory fallback.",
      error,
    );
    return noopStorage;
  }
};
const storage = resolveStorage();

export const UserContext = createContext({
  token: "",
  isLoggedIn: false,
  userSet: () => {},
  user: undefined,
  logout: () => {},
  login: () => {},
});

const calculateRemainingTime = (expirationTime) => {
  const currentTime = new Date().getTime();
  const adjExpirationTime = new Date(expirationTime).getTime();

  return adjExpirationTime - currentTime;
};

const retrieveStoredToken = () => {
  const userToken = storage.getItem("RediHomeToken");
  const storedExpirationDate = storage.getItem("ExpirationTime");

  if (!userToken || !storedExpirationDate) {
    return null;
  }

  const remainingTime = calculateRemainingTime(storedExpirationDate);

  if (remainingTime <= 3600) {
    storage.removeItem("RediHomeToken");
    storage.removeItem("ExpirationTime");
    storage.removeItem("RediHomeUser");
    return null;
  }

  return {
    token: userToken,
    duration: remainingTime,
  };
};

const retrieveStoredUser = () => {
  const user = storage.getItem("RediHomeUser");

  return user;
};

const removeStoredUser = () => {
  storage.removeItem("RediHomeUser");
  return null;
};

export const getBoolStorage = (item) => {
  return storage.getItem(item) === "true";
};

export const UserProvider = (props) => {
  const tokenData = retrieveStoredToken();
  const userData = retrieveStoredUser();

  let initialToken;
  if (tokenData) {
    initialToken = tokenData.token;
  }
  let initialUser;
  if (userData) {
    initialUser = JSON.parse(userData);
  }

  const [token, setToken] = useState(initialToken);
  const [user, setUser] = useState(initialUser);

  const userIsLoggedIn = !!token;

  const logoutHandler = useCallback(() => {
    setToken(null);
    setUser(removeStoredUser());
    storage.removeItem("RediHomeToken");
    storage.removeItem("ExpirationTime");
    storage.removeItem("RediHomeUser");
    if (logoutTimer) {
      clearTimeout(logoutTimer);
    }
  }, []);

  const userSetHandler = async () => {
    let user = await getMyData(token);
    if (user?.isOk) {
      setUser(user.data);
      storage.setItem("RediHomeUser", JSON.stringify(user.data));
    }
  };

  const loginHandler = async (token, expirationTime) => {
    setToken(token);
    storage.setItem("RediHomeToken", token);
    storage.setItem("ExpirationTime", expirationTime);

    const remainingTime = calculateRemainingTime(expirationTime);

    logoutTimer = setTimeout(logoutHandler, remainingTime);
    try {
      let user = await getMyData(token);
      if (user.isOk) {
        setUser(user.data);
        storage.setItem("RediHomeUser", JSON.stringify(user.data));
      }
      return user?.data;
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  };

  useEffect(() => {
    if (tokenData) {
      logoutTimer = setTimeout(logoutHandler, tokenData.duration);
    }
  }, [tokenData, logoutHandler]);

  const contextValue = {
    user: user,
    token: token,
    isLoggedIn: userIsLoggedIn,
    login: loginHandler,
    logout: logoutHandler,
    userSet: userSetHandler,
  };

  return (
    <UserContext.Provider value={contextValue}>
      {props?.children}
    </UserContext.Provider>
  );
};
